/**
 * Importar3DPanel — slide-over "Importar projeto 3D" (Orcamento v2).
 *
 * Fiel ao mockup "Orcamento", mas com FONTES REAIS: os cards de
 * origem vem de GET /api/cad/adapters (DinaBox API / DinaBox XML / futuros
 * adapters via CADIntegration) e a lista de projetos vem da CONTA DO CLIENTE
 * (GET /api/cad/projetos — DinaBox API), nao dos exemplos do mockup.
 *
 * Adapter de API: busca com debounce + selecao + Importar.
 * Adapter de arquivo: input de upload (.xml exportado do DinaBox).
 *
 * CSS: classes .orc2-panel (styles/canonical/orcamento-v2.css).
 */
import React, { useEffect, useRef, useState } from "react";
import { FiBox, FiSearch, FiUploadCloud } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  importarProjeto3D,
  listarAdaptersCad,
  listarProjetosCad,
} from "../../../services/cad";
import { EmptyMsg, ErrorMsg } from "../styles";

const STATUS_PROJETO = [
  { value: "", label: "Todos status" },
  { value: "rascunho", label: "Rascunho" },
  { value: "producao", label: "Produção" },
  { value: "fechado", label: "Fechado" },
];

function Importar3DPanel({ open, onClose, orcamento, onImported }) {
  const { request } = useAuth();
  const [adapters, setAdapters] = useState([]);
  const [adapterSel, setAdapterSel] = useState("");
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("");
  const [projetos, setProjetos] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [projetoSel, setProjetoSel] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const [erro, setErro] = useState(null);
  const fileRef = useRef(null);

  // Payload de /api/cad/adapters: {adapter_id, nome, aceita: ["xml"|"api"...],
  // requer_configuracao}. Adapter de API lista projetos da conta; adapter de
  // arquivo recebe upload com as extensoes de `aceita`.
  const ehAdapterApi = (a) => (a?.aceita || []).includes("api");
  const extensoesArquivo = (a) =>
    (a?.aceita || []).filter((ext) => ext !== "api");
  const adapterAtual = adapters.find((a) => a.adapter_id === adapterSel) || null;
  const modoApi = Boolean(adapterAtual && ehAdapterApi(adapterAtual));

  // Adapters carregados quando o painel abre (lazy) + reset de estado.
  useEffect(() => {
    if (!open) return;
    setErro(null);
    setProjetoSel(null);
    setArquivo(null);
    setBusca("");
    setStatus("");
    listarAdaptersCad(request)
      .then((payload) => {
        const items = payload?.adapters || [];
        setAdapters(items);
        // Preferimos o adapter de API (lista projetos da conta do cliente).
        const api = items.find((a) => (a?.aceita || []).includes("api"));
        setAdapterSel(api?.adapter_id || items[0]?.adapter_id || "");
      })
      .catch(() => setAdapters([]));
  }, [open, request]);

  // Lista de projetos da conta (adapter de API) — busca com debounce.
  useEffect(() => {
    if (!open || !modoApi || !adapterSel) {
      setProjetos([]);
      setTotal(0);
      return undefined;
    }
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const id = setTimeout(() => {
      listarProjetosCad(request, {
        adapter: adapterSel,
        unidade: orcamento?.unidade,
        page: 1,
        qty: 20,
        search: busca.trim(),
        status,
      })
        .then((payload) => {
          if (!ativo) return;
          setProjetos(payload?.projetos || []);
          setTotal(payload?.total ?? (payload?.projetos || []).length);
        })
        .catch((err) => {
          if (!ativo) return;
          setProjetos([]);
          setTotal(0);
          setErro(err?.message || "Falha ao listar projetos do 3D.");
        })
        .finally(() => ativo && setCarregando(false));
    }, 350);
    return () => {
      ativo = false;
      clearTimeout(id);
    };
  }, [open, modoApi, adapterSel, busca, status, orcamento?.unidade, request]);

  const importar = async () => {
    setErro(null);
    if (modoApi && !projetoSel) {
      setErro("Selecione um projeto da lista.");
      throw new Error("Projeto não selecionado.");
    }
    if (!modoApi && !arquivo) {
      setErro("Escolha o arquivo exportado do 3D.");
      throw new Error("Arquivo não selecionado.");
    }
    await importarProjeto3D(request, orcamento.id, {
      adapter: adapterSel,
      projetoExternoId: modoApi ? String(projetoSel.id) : undefined,
      arquivo: modoApi ? undefined : arquivo,
    });
    await onImported();
    onClose();
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Importar projeto 3D"
      subtitle="Pré-popula ambientes, móveis e medidas"
      width="min(95vw, 620px)"
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancelar
          </button>
          <AsyncButton
            onClick={importar}
            silentSuccess
            silentError
            disabled={modoApi ? !projetoSel : !arquivo}
          >
            <FiUploadCloud size={14} aria-hidden="true" /> Importar
          </AsyncButton>
        </>
      }
    >
      <div className="orc2-panel">
        <div className="so-section-label">De onde importar</div>
        {adapters.length === 0 ? (
          <EmptyMsg style={{ padding: 8 }}>Carregando fontes...</EmptyMsg>
        ) : (
          <div className="src-grid">
            {adapters.map((a) => (
              <button
                key={a.adapter_id}
                type="button"
                className={"src-pick" + (a.adapter_id === adapterSel ? " is-sel" : "")}
                onClick={() => {
                  setAdapterSel(a.adapter_id);
                  setProjetoSel(null);
                  setArquivo(null);
                }}
              >
                <span className="si">
                  <FiBox size={16} aria-hidden="true" />
                </span>
                <span className="sn">{a.nome || a.adapter_id}</span>
                <span className="sd">
                  {ehAdapterApi(a)
                    ? "conta conectada"
                    : `.${extensoesArquivo(a).join(" / .")} exportado`}
                </span>
              </button>
            ))}
          </div>
        )}

        {modoApi ? (
          <>
            <div className="so-section-label">Projetos disponíveis</div>
            <div className="row-flex" style={{ marginBottom: 10 }}>
              <div className="input-prefixed" style={{ flex: 1 }}>
                <FiSearch size={14} aria-hidden="true" />
                <input
                  type="search"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar projeto 3D por nome ou cliente..."
                />
              </div>
              <select
                className="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ width: 150 }}
                aria-label="Filtrar por status"
              >
                {STATUS_PROJETO.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {carregando ? (
              <EmptyMsg style={{ padding: 10 }}>Buscando projetos na sua conta...</EmptyMsg>
            ) : projetos.length === 0 ? (
              <EmptyMsg style={{ padding: 10 }}>
                {busca.trim()
                  ? `Nenhum projeto encontrado para "${busca.trim()}".`
                  : "Nenhum projeto disponível na conta conectada."}
              </EmptyMsg>
            ) : (
              <>
                {projetos.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={
                      "proj-3d" + (projetoSel?.id === p.id ? " is-sel" : "")
                    }
                    onClick={() => setProjetoSel(p)}
                  >
                    <span className="thumb">
                      <FiBox size={16} aria-hidden="true" />
                    </span>
                    {/* O AMBIENTE em destaque; cliente e código embaixo.
                        Numa conta com dez projetos chamados "COZINHA", o
                        nome sozinho não distingue nada — quem distingue é
                        o cliente. */}
                    <span className="pj">
                      <span className="n" title={p.nome || `Projeto ${p.id}`}>
                        {p.nome || `Projeto ${p.id}`}
                      </span>
                      <span className="s">
                        {p.cliente
                          ? p.cliente
                          : <span className="sem-cliente">sem cliente</span>}
                        {" · "}
                        <span className="cod">#{p.id}</span>
                        {p.status ? ` · ${p.status}` : ""}
                      </span>
                    </span>
                  </button>
                ))}
                <p className="muted-mono" style={{ marginTop: 8 }}>
                  Exibindo {projetos.length} de {total} projeto{total === 1 ? "" : "s"}.
                  {projetoSel ? ` Selecionado: #${projetoSel.id}` : ""}
                </p>
              </>
            )}
          </>
        ) : (
          adapterAtual && (
            <>
              <div className="so-section-label">Arquivo</div>
              <input
                ref={fileRef}
                type="file"
                accept={extensoesArquivo(adapterAtual)
                  .map((e) => `.${e.replace(/^\./, "")}`)
                  .join(",")}
                onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                className="input"
              />
              <p className="muted-mono" style={{ marginTop: 8 }}>
                Exporte o projeto no 3D (
                {extensoesArquivo(adapterAtual).join(" / ").toUpperCase() || "XML"}
                ) — 1 arquivo = 1 ambiente novo no orcamento.
              </p>
            </>
          )
        )}

        <div className="so-banner" style={{ marginTop: 14 }}>
          <strong>Preços:</strong> os valores virão zerados até o estoque precificar —
          o almoxarifado/estoque do PharuERP é a fonte da verdade dos preços.
        </div>

        {erro && <ErrorMsg style={{ marginTop: 10 }}>{erro}</ErrorMsg>}
      </div>
    </SlideOverPanel>
  );
}

export default Importar3DPanel;
